/* NONNA video hero: picks the right video for the screen, autoplays muted, pauses off-screen,
   respects reduced motion / Save-Data, and drives the brewing-steps bar. */
if (!customElements.get('nonna-video-hero')) {
  customElements.define(
    'nonna-video-hero',
    class NonnaVideoHero extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return this.attachGlobal(); // moved in the theme editor: only re-attach outside listeners
        this.initialized = true;
        this.video = this.querySelector('.nvh__video');
        this.toggle = this.querySelector('.nvh__toggle');
        this.chapters = [...this.querySelectorAll('.nvh__ch')].map((btn) => ({
          btn,
          start: parseFloat(btn.dataset.start) || 0,
          fill: btn.querySelector('.nvh__bar i'),
        }));
        this.phone = window.matchMedia('(max-width: 749px)');
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.saveData = !!(navigator.connection && navigator.connection.saveData);
        this.userPaused = reduceMotion || this.saveData;
        this.onScreen = true;
        this.raf = 0;

        this.onResize = () => this.setOffset();
        if (!this.video) return this.attachGlobal();

        this.video.muted = true;
        this.onMedia = () => this.load();

        // Reveal the video over the poster only once it shows the right frame (after any start-at jump)
        const showVideo = () => this.classList.add('has-video');
        this.video.addEventListener('playing', () => {
          showVideo();
          cancelAnimationFrame(this.raf);
          this.tick();
        });
        this.video.addEventListener('pause', () => {
          cancelAnimationFrame(this.raf);
          this.render();
        });
        this.video.addEventListener('seeked', () => {
          showVideo();
          this.render();
        });
        this.video.addEventListener('loadedmetadata', () => this.render());

        this.toggle?.addEventListener('click', () => {
          this.userPaused = !this.userPaused;
          if (!this.userPaused) this.ensureSource();
          this.sync();
        });
        this.chapters.forEach((c) =>
          c.btn.addEventListener('click', () => {
            this.seekTo(c.start + 0.01);
            this.userPaused = false;
            this.sync();
            this.render();
          })
        );

        // Theme editor: selecting a step block jumps the video to that step
        this.onBlockSelect = (event) => {
          const chapter = this.chapters.find((c) => c.btn.dataset.blockId === event.detail?.blockId);
          if (!chapter) return;
          this.seekTo(chapter.start + 0.01);
          this.render();
        };

        this.observer = new IntersectionObserver(
          ([entry]) => {
            this.onScreen = entry.isIntersecting;
            this.sync();
          },
          { threshold: 0.1 }
        );

        this.attachGlobal();
        this.render();
        this.load();
      }

      // Listeners outside this element; removed on disconnect so nothing doubles up
      attachGlobal() {
        this.setOffset();
        window.addEventListener('resize', this.onResize);
        if (!this.video) return;
        this.phone.addEventListener('change', this.onMedia);
        document.addEventListener('shopify:block:select', this.onBlockSelect);
        this.observer.observe(this);
      }

      disconnectedCallback() {
        cancelAnimationFrame(this.raf);
        window.removeEventListener('resize', this.onResize);
        if (!this.video) return;
        this.video.pause();
        this.observer.disconnect();
        this.phone.removeEventListener('change', this.onMedia);
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
      }

      // Hero fills the screen below the header/announcement bar
      setOffset() {
        const top = this.getBoundingClientRect().top + window.scrollY;
        this.style.setProperty('--nvh-top', `${Math.max(0, Math.round(top))}px`);
      }

      sourceFor() {
        return this.phone.matches ? this.video.dataset.srcMobile : this.video.dataset.srcDesktop;
      }

      // Where this screen size should start (e.g. phones open on the pour shot)
      startFor() {
        return parseFloat(this.phone.matches ? this.video.dataset.startMobile : this.video.dataset.startDesktop) || 0;
      }

      // Load the right file for this screen. Keeps the current position when swapping files,
      // otherwise starts at `time` or the start-at setting. Returns true if a new file is loading.
      ensureSource(time) {
        const src = this.sourceFor();
        if (!src || this.video.getAttribute('src') === src) return false;
        this.pendingTime = time ?? (this.video.currentTime || this.startFor());
        this.video.preload = 'auto';
        this.video.src = src;
        this.video.addEventListener(
          'loadedmetadata',
          () => {
            if (this.pendingTime) this.video.currentTime = this.pendingTime;
            this.pendingTime = 0;
          },
          { once: true }
        );
        return true;
      }

      seekTo(time) {
        if (this.ensureSource(time)) return;
        if (this.video.readyState < 1) this.pendingTime = time; // still loading: jump there once it can
        else this.video.currentTime = time;
      }

      load() {
        if (this.saveData) return this.paint(); // keep the poster only on data-saver connections
        this.ensureSource();
        if (this.userPaused) this.video.preload = 'metadata'; // reduced motion: don't pull the whole file until asked
        this.sync();
      }

      // Play only when the visitor hasn't paused and the hero is on screen
      sync() {
        if (!this.userPaused && this.onScreen && this.video.getAttribute('src')) {
          this.video.play().catch((err) => {
            if (err.name === 'NotAllowedError') {
              this.userPaused = true; // autoplay blocked (e.g. iOS Low Power Mode)
              this.paint();
            }
          });
        } else {
          this.video.pause();
        }
        this.paint();
      }

      paint() {
        this.classList.toggle('is-paused', this.userPaused);
        this.toggle?.setAttribute('aria-label', this.userPaused ? 'Play background video' : 'Pause background video');
      }

      render() {
        if (!this.chapters.length) return;
        const t = this.video.currentTime;
        const duration = this.video.duration || 0;
        this.chapters.forEach((c, i) => {
          const next = this.chapters[i + 1];
          const end = next ? next.start : duration || c.start + 1;
          const progress = Math.min(1, Math.max(0, (t - c.start) / Math.max(0.001, end - c.start)));
          c.fill.style.transform = `scaleX(${progress})`;
          c.btn.classList.toggle('is-active', t >= c.start && t < end);
        });
      }

      tick() {
        this.render();
        this.raf = requestAnimationFrame(() => this.tick());
      }
    }
  );
}
